import React, { useState, useMemo, useEffect } from "react";
import { SortField, SortOrder } from "../../types/Campaign";
import { FilterBar } from "../molecules/FilterBar";
import { ListHeader } from "../molecules/ListHeader";
import { MobileSortBar } from "../molecules/MobileSortBar";
import { CampaignList } from "../organisms/CampaignList";
import {
  getUniqueBrands,
  filterCampaignsByBrand,
  filterCampaignsBySearch,
  filterCampaignsBySquad,
} from "../../utils/campaignUtils";
import { useCampaigns } from "../../hooks/useCampaigns";
import { SquadFilterValue } from "../molecules/SquadFilter";

export const CampaignsPage: React.FC = () => {
  const { campaigns, loading, error } = useCampaigns();
  const [sortField, setSortField] = useState<SortField>("startDate");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [brandFilter, setBrandFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [squadFilter, setSquadFilter] = useState<SquadFilterValue>(() => {
    const saved = localStorage.getItem('squadFilter');
    return (saved as SquadFilterValue) || 'all';
  });

  console.log(campaigns);

  useEffect(() => {
    localStorage.setItem('squadFilter', squadFilter);
    setBrandFilter('all');
    setSearchQuery('');
  }, [squadFilter]);

  const availableBrands = useMemo(() => {
    const squadFiltered = filterCampaignsBySquad(campaigns, squadFilter);
    return getUniqueBrands(squadFiltered);
  }, [campaigns, squadFilter]);

  const filteredCampaigns = useMemo(() => {
    let filtered = filterCampaignsBySquad(campaigns, squadFilter);
    filtered = filterCampaignsByBrand(filtered, brandFilter);
    filtered = filterCampaignsBySearch(filtered, searchQuery);
    return filtered;
  }, [campaigns, brandFilter, searchQuery, squadFilter]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  if (error) {
    return (
      <div className="p-6 sm:p-10">
        <div className="text-center p-8 bg-white/80 dark:bg-dark-300/80 backdrop-blur-sm rounded-3xl shadow-soft border-0 max-w-md mx-auto">
          <div className="text-red-600 dark:text-red-400 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-soft-black dark:text-gray-100 mb-2">
            Erro ao carregar campanhas
          </h2>
          <p className="text-soft-gray dark:text-gray-400 mb-4">
            {error.message}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-soft-black hover:bg-soft-gray text-white font-medium rounded-2xl transition-all duration-300 shadow-soft hover:shadow-soft-lg"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-10">
      <header className="mb-6 sm:mb-10">
        <h1 className="text-2xl sm:text-4xl font-bold text-soft-black dark:text-gray-100 mb-1 sm:mb-2">
          Campanhas
        </h1>
        <p className="text-sm sm:text-base text-soft-gray dark:text-gray-400">
          Acompanhamento dos resultados das campanhas de Poste e Ganhe
        </p>
      </header>

        {loading && campaigns.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-warm-500 dark:border-dark-purple-300 mb-4"></div>
              <p className="text-soft-gray dark:text-gray-400 font-medium">
                Carregando campanhas...
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-white/80 dark:bg-dark-300/80 backdrop-blur-sm rounded-3xl shadow-soft border-0 p-4 sm:p-6 mb-6 sm:mb-8">
              <FilterBar
                brandFilter={brandFilter}
                onBrandFilterChange={setBrandFilter}
                availableBrands={availableBrands}
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                squadFilter={squadFilter}
                onSquadFilterChange={setSquadFilter}
              />
            </div>

            <div className="mb-4 sm:mb-5">
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                Exibindo {filteredCampaigns.length} campanha
                {filteredCampaigns.length !== 1 ? "s" : ""}
              </p>
            </div>

            <ListHeader
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
            />

            <MobileSortBar
              sortField={sortField}
              sortOrder={sortOrder}
              onSortFieldChange={setSortField}
              onSortOrderChange={setSortOrder}
            />

            <CampaignList
              campaigns={filteredCampaigns}
              sortField={sortField}
              sortOrder={sortOrder}
            />
          </>
        )}
    </div>
  );
};
